// The microphone choice, as a row in Session options (Adam, 14 Sep). It used
// to be a caret beside the mic in the composer, which the composer's control
// row turned into a second white disc. Props only: the page owns the list and
// the choice through useMicrophones, and both dialogs render this same row.
import React from 'react'
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material'
import type { AudioDevice } from '../../agent'

// Not '' (MUI's Select draws an empty string as nothing at all) and not
// 'default' (Chrome lists a real device under that id, labelled "Default - …").
const SYSTEM_DEFAULT = '__system_default__'

export interface MicrophoneSelectProps {
  microphones: AudioDevice[]
  /** null is the system default. */
  microphoneId: string | null
  onMicrophoneChange: (deviceId: string | null) => void
  disabled?: boolean
}

export default function MicrophoneSelect({ microphones, microphoneId, onMicrophoneChange, disabled }: MicrophoneSelectProps) {
  // A choice the browser no longer lists (an unplugged headset) shows as the
  // default rather than as an empty box; the next recording falls back anyway.
  const known = microphoneId && microphones.some(m => m.deviceId === microphoneId) ? microphoneId : SYSTEM_DEFAULT
  return (
    <FormControl size="small" fullWidth>
      <InputLabel id="microphone-select-label">Microphone</InputLabel>
      <Select
        labelId="microphone-select-label"
        value={known}
        onChange={e => onMicrophoneChange(e.target.value === SYSTEM_DEFAULT ? null : String(e.target.value))}
        label="Microphone"
        disabled={disabled}
        data-testid="microphone-select"
      >
        <MenuItem value={SYSTEM_DEFAULT}>System default</MenuItem>
        {microphones.map(m => (
          <MenuItem key={m.deviceId} value={m.deviceId}>{m.label}</MenuItem>
        ))}
      </Select>
      {microphones.length === 0 && (
        <FormHelperText>Your microphones are listed after the first dictation.</FormHelperText>
      )}
    </FormControl>
  )
}
